// ViewAllActivityModal.js - Component for viewing all deposit history
import React, { useState, useEffect } from 'react';
import { X, Plus, Calendar, Filter, Search, Gift } from 'lucide-react';
import { Button } from '../components/ui';

export const ViewAllActivityModal = ({ 
  isOpen, 
  onClose, 
  child,
  initialDeposits = []
}) => {
  const [deposits, setDeposits] = useState(initialDeposits);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, allowance, chores, bonus, etc.
  const [sortOrder, setSortOrder] = useState('newest'); // newest, oldest, amount-high, amount-low

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app';

  useEffect(() => {
    if (isOpen && child?.id) {
      loadAllDeposits();
    }
  }, [isOpen, child?.id]);

  const loadAllDeposits = async () => {
    if (!child?.id) return;
    
    setLoading(true);
    try {
      // Load all verified/approved deposits (increase limit to get all)
      const response = await fetch(`${API_BASE_URL}/api/deposits/history/${child.id}?limit=100`);
      const result = await response.json();
      
      if (result.success) {
        setDeposits(result.data || []);
      }
    } catch (error) {
      console.error('Error loading all deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedDeposits = () => {
    let filtered = deposits;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(deposit => 
        (deposit.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (deposit.source || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(deposit => {
        const description = (deposit.description || deposit.source || '').toLowerCase();
        switch (filterType) {
          case 'allowance': return description.includes('allowance');
          case 'chores': return description.includes('chore') || description.includes('help');
          case 'bonus': return parseFloat(deposit.bonus_earned || 0) > 0;
          case 'learning': return description.includes('learn') || description.includes('lesson');
          default: return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case 'oldest':
          return new Date(a.created_at || a.verified_at) - new Date(b.created_at || b.verified_at);
        case 'amount-high':
          return parseFloat(b.amount || 0) - parseFloat(a.amount || 0);
        case 'amount-low':
          return parseFloat(a.amount || 0) - parseFloat(b.amount || 0);
        case 'newest':
        default:
          return new Date(b.created_at || b.verified_at) - new Date(a.created_at || a.verified_at);
      }
    });

    return filtered;
  };

  const getTotalStats = () => {
    const filtered = filteredAndSortedDeposits();
    const totalAmount = filtered.reduce((sum, deposit) => sum + parseFloat(deposit.amount || 0), 0);
    const totalBonus = filtered.reduce((sum, deposit) => sum + parseFloat(deposit.bonus_earned || 0), 0);
    return { totalAmount, totalBonus, count: filtered.length };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDepositTypeIcon = (deposit) => {
    const description = (deposit.description || deposit.source || '').toLowerCase();
    const hasBonus = parseFloat(deposit.bonus_earned || 0) > 0;
    
    if (hasBonus) return '🌟';
    if (description.includes('allowance')) return '💰';
    if (description.includes('chore') || description.includes('help')) return '🧹';
    if (description.includes('learn') || description.includes('lesson')) return '📚';
    return '💵';
  };

  if (!isOpen) return null;

  const stats = getTotalStats();
  const filteredDeposits = filteredAndSortedDeposits();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-green-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">All Activity</h3>
              <p className="text-sm text-gray-600">{child?.first_name}'s complete deposit history</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Summary */}
        <div className="p-6 bg-gray-50 border-b border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{stats.count}</div>
              <div className="text-sm text-gray-600">Total Deposits</div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">${stats.totalAmount.toFixed(2)}</div>
              <div className="text-sm text-gray-600">Total Amount</div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">${stats.totalBonus.toFixed(2)}</div>
              <div className="text-sm text-gray-600">Bonus Earned</div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search deposits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            {/* Filter by Type */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="allowance">Allowance</option>
              <option value="chores">Chores & Help</option>
              <option value="bonus">With Bonus</option>
              <option value="learning">Learning</option>
            </select>

            {/* Sort Order */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="amount-high">Highest Amount</option>
              <option value="amount-low">Lowest Amount</option>
            </select>
          </div>
        </div>

        {/* Deposit List */}
        <div className="flex-1 overflow-y-auto max-h-96">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              <span className="ml-3 text-gray-600">Loading deposits...</span>
            </div>
          ) : filteredDeposits.length > 0 ? (
            <div className="p-6 space-y-3">
              {filteredDeposits.map((deposit, index) => {
                const depositAmount = parseFloat(deposit.amount || 0);
                const bonusEarned = parseFloat(deposit.bonus_earned || 0);
                const totalValue = depositAmount + bonusEarned;
                
                return (
                  <div key={deposit.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-4">
                      {/* Icon */}
                      <div className="text-2xl">
                        {getDepositTypeIcon(deposit)}
                      </div>
                      
                      {/* Details */}
                      <div>
                        <div className="font-medium text-gray-900">
                          {deposit.description || deposit.source || 'Deposit'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center space-x-2">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(deposit.created_at || deposit.verified_at)}</span>
                          {bonusEarned > 0 && (
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                              Bonus
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Amount */}
                    <div className="text-right">
                      {bonusEarned > 0 ? (
                        <div>
                          <div className="text-lg font-bold text-emerald-600">
                            +${totalValue.toFixed(2)}
                          </div>
                          <div className="text-xs text-yellow-600 font-medium">
                            +${bonusEarned.toFixed(2)} bonus
                          </div>
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-emerald-600">
                          +${depositAmount.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-4xl mb-4">📈</div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filterType !== 'all' ? 'No matching deposits' : 'No deposits yet'}
              </h4>
              <p className="text-gray-600 text-center max-w-md">
                {searchTerm || filterType !== 'all' 
                  ? 'Try adjusting your search or filter settings.'
                  : 'Start saving money to see your deposit history here!'
                }
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {filteredDeposits.length} of {deposits.length} deposits
            </div>
            <Button
              onClick={onClose}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};